import * as React from 'react'

import { AccessibleVideo, } from '../../../../../Shared/index'
import {
  COLD_WAR_AN_OVERVIEW_HEADER,
  COMMENT_DOCUMENT_ICON,
  BRITISH_WOMEN_WORKERS_EVIDENCE_ACTIVITY_ANIMATION,
  BRITISH_WOMEN_WORKERS_EVIDENCE_ACTIVITY_ON_LAPTOP,
  HOW_DID_BANANAS_LEAD_TO_A_COUP_IN_GUATEMALA_HEADER,
  HOW_DID_HAITI_BECOME_THE_FIRST_INDEPENDENT_BLACK_REPUBLIC_HEADER,
  OER_PROJECT_ICON,
  OER_PROJECT_LOGO,
  QUILL_FULL_LOGO,
  QUILL_ICON,
  RESEARCH_BOOKS_EXTRA_LARGE_ICON,
  SEARCH_DOCUMENT_ICON,
  STUDENT_AND_TEACHER_ILLUSTRATION,
  SYNC_ICON,
  TIMELINE,
  WHY_DID_FRANCE_INTERVENE_IN_THE_AMERICAN_REVOLUTION_HEADER,
  WHY_DID_REVOLUTIONS_ERUPT_THROUGH_THE_ATLANTIC_WORLD_HEADER,
} from './asset_constants'
import FinalSlide from './shared_slides/final_slide'
import ImmediateActionableFeedbackSlide from './shared_slides/immediate_actionable_feedback_slide'

const slideGroupClassName = 'social-studies-slide'

const teachWritingAndCoverContentSlide = (
  <section className={`slide ${slideGroupClassName} teach-writing-and-cover-content-slide`} key="teach-writing-and-cover-content-slide">
    <h1>Teach Writing and Cover Content</h1>
    <p>
      Quill’s first Social Studies offering is a suite of World History activities designed for 8th-12th grade students. Social Studies teachers often use Quill once per week for a 15-20 minute activity.
      <br /><br />
      Each activity, written and reviewed by Social Studies educators, highlights a moment or movement in World History linked to course themes. Students build course knowledge while engaging in:
    </p>
    <div className="grid-wrapper">
      <div>
        <img alt="" src={SEARCH_DOCUMENT_ICON} />
        <h2>Close reading</h2>
        <p>Every activity begins with a structured highlighting task as an entry point to the text. Then, students read and reread up to ten times in the process of developing their responses.</p>
      </div>
      <div>
        <img alt="" src={RESEARCH_BOOKS_EXTRA_LARGE_ICON} />
        <h2>Constructing evidence-based claims</h2>
        <p>Students receive custom feedback developed by real teachers to help them use what they have read to craft strong sentences.</p>
      </div>
      <div>
        <img alt="" src={COMMENT_DOCUMENT_ICON} />
        <h2>Sentence fundamentals</h2>
        <p>Practice with conjunctions like because, but, and so provides students with a framework to express complex ideas. Once they’ve captured these ideas in their response, students receive grammar and spelling guidance too.</p>
      </div>
    </div>
  </section>
)

const worldHistoryContentCoveredSlide = (
  <section className={`slide ${slideGroupClassName} world-history-content-covered-slide`} key="world-history-content-covered-slide">
    <h1>World History Content Covered</h1>
    <p>
      Quill's new World History activities provide structured opportunities to read and write about key moments and movements—all while receiving responsive, teacher-developed feedback.
      <br /><br />
      Quill provides a full year sequence from 1200 CE to the Present and 1750 CE to the present.
    </p>
    <img
      alt="Historical timeline showing major periods from 1250 CE to Present, including The Global Tapestry, Transoceanic Connections, Revolutions, Industrialization, New Imperialism, Cold War, Global Conflict, and Globalization (marked as Coming Soon)."
      src={TIMELINE}
    />
    <p className="image-caption">These are the topics covered in the 1200 CE to the Present sequence.</p>
  </section>
)

const howEachUnitSlide = (
  <section className={`slide ${slideGroupClassName} how-each-unit-slide`} key="how-each-unit-slide">
    <h1>How Each Unit Builds Knowledge and Skills</h1>
    <p>
      Each instructional unit covers one period of history and provides three learning activities for students.
      <br /><br />
      Each activity leverages the <b>Quill Reading for Evidence</b> tool to enable students to analyze texts, identify claims, and support ideas with evidence.
    </p>
    <div className="divider" />
    <p>In Unit 3, students engage in three activities about the <b>18th Century Revolutions</b>:</p>
    <div className="grid-wrapper">
      <div className="activity-card">
        <img alt="" src={WHY_DID_REVOLUTIONS_ERUPT_THROUGH_THE_ATLANTIC_WORLD_HEADER} />
        <h2>Activity #1</h2>
        <p>Why Did Revolutions Erupt Throughout the Atlantic World?</p>
      </div>
      <div className="activity-card">
        <img alt="" src={WHY_DID_FRANCE_INTERVENE_IN_THE_AMERICAN_REVOLUTION_HEADER} />
        <h2>Activity #2</h2>
        <p>Why Did France Intervene in the American Revolution?</p>
      </div>
      <div className="activity-card">
        <img alt="" src={HOW_DID_HAITI_BECOME_THE_FIRST_INDEPENDENT_BLACK_REPUBLIC_HEADER} />
        <h2>Activity #3</h2>
        <p>How Did Haiti Become the First Independent Black Republic?</p>
      </div>
    </div>
  </section>
)

const quillPlusOERSlide = (
  <section className={`slide ${slideGroupClassName} quill-plus-oer-slide`} key="quill-plus-oer-slide">
    <h1><img alt="Quill.org" src={QUILL_FULL_LOGO} /> + <img alt="OER Project" src={OER_PROJECT_LOGO} /></h1>
    <p>
      Quill has partnered with <a href="https://www.oerproject.com/" rel="noopener noreferrer" target="_blank">OER Project</a> to curate open educational resources from their site that work well with Quill social studies content.
      <br /><br />
      Each Reading for Evidence activity includes a paired OER Project text (600L–1500L). Some teachers begin with the OER text and use Quill to go deeper, while others pair Quill with their own curriculum.
    </p>
    <div className="grid-wrapper">
      <div className="activity-card">
        <img alt="" src={HOW_DID_BANANAS_LEAD_TO_A_COUP_IN_GUATEMALA_HEADER} />
        <h2>Quill Reading for Evidence</h2>
        <p>How Did Bananas Lead to a Coup in Guatemala?</p>
      </div>
      <div>
        <img alt="" src={QUILL_ICON} />
        <img alt="" src={SYNC_ICON} />
        <img alt="" src={OER_PROJECT_ICON} />
      </div>
      <div className="activity-card">
        <img alt="" src={COLD_WAR_AN_OVERVIEW_HEADER} />
        <h2>OER Project Text</h2>
        <p>Cold War: An Overview</p>
      </div>
    </div>
    <p>These texts can be used to preview new content, reinforce prior knowledge, provide additional historical context, or, when used in conjunction with the Quill activity, serve as part of a text set to support extended writing practice.</p>
  </section>
)

const immediateActionableFeedbackSlide = (
  <ImmediateActionableFeedbackSlide
    firstParagraph={<p>Students read a text and then complete three writing prompts. Each time they receive immediate feedback to revise and strengthen their writing.</p>}
    media={
      <div className="tool-on-laptop">
        <img
          alt="Screenshot of a Quill Reading for Evidence activity full-screen on a laptop"
          loading="eager"
          src={BRITISH_WOMEN_WORKERS_EVIDENCE_ACTIVITY_ON_LAPTOP}
        />
        <div className='video-section'>
          <AccessibleVideo
            ariaLabel="Animation of a student typing a response to an Evidence prompt and receiving feedback on it"
            src={BRITISH_WOMEN_WORKERS_EVIDENCE_ACTIVITY_ANIMATION}
          />
        </div>
      </div>
    }
    slideGroupClassName={slideGroupClassName}
  />
)

const quillsAIIsUniqueSlide = (
  <section className={`slide ${slideGroupClassName} quills-ai-is-unique-slide`} key="quills-ai-is-unique-slide">
    <h1>Quill’s AI is Unique</h1>
    <p>
      No other EdTech tool builds unique <b>teacher-written feedback</b> for every writing prompt.
    </p>
    <div className="flex-wrapper">
      <p>
        <b>Quill's AI is trained by:</b>
        <span className="divider" />
        <b>Real</b> student responses
        <br />
        <b>Real</b> teacher feedback
      </p>
      <img alt="" src={STUDENT_AND_TEACHER_ILLUSTRATION} />
      <p>
        <b>The result:</b>
        <span className="divider" />
        Authentic,<br />
        sentence-level feedback<br />
        (without multiple choice)
      </p>
    </div>
    <p>For each prompt, Quill curriculum developers manually categorize <b>1,000 student responses</b> and create over <b>50 custom feedback options</b> addressing common issues. This personalized coaching strengthens students' writing, offering <b>up to five rounds of feedback per prompt.</b></p>
  </section>
)

const ourAimSocialStudiesSlide = (
  <section className={`slide ${slideGroupClassName} our-aim-social-studies-slide`} key="our-aim-social-studies-slide">
    <div>
      <h1>Our aim is to support you, not replace you</h1>
      <div className="grid-wrapper">
        <div>
          <p className="quote">“Quill has been helpful in giving them a toolkit to make arguments [and] to identify and test claims.”</p>
          <div className="teacher-name-and-info">
            <p className="name">Rachel R.</p>
            <p className="info">Modern World History teacher<br /> in State Fine Arts High School</p>
          </div>
        </div>
        <div>
          <p className="quote">“They're learning content that I would probably not have time to teach them. They are practicing skills that, honestly, they all need more practice on every day.“</p>
          <div className="teacher-name-and-info">
            <p className="name">Amber L.</p>
            <p className="info">Glasgow School District</p>
          </div>
        </div>
        <div>
          <p className="quote">“I would say that the combination of using the OER curriculum and Quill has pushed them in their reading stamina and level.”</p>
          <div className="teacher-name-and-info">
            <p className="name">Tricia C.</p>
            <p className="info">Latexo High School, 9th Grade<br /> World History</p>
          </div>
        </div>
      </div>
    </div>
  </section>
)

const socialStudiesSlides = ({ getPremiumCredit, }) => [
  teachWritingAndCoverContentSlide,
  worldHistoryContentCoveredSlide,
  howEachUnitSlide,
  quillPlusOERSlide,
  immediateActionableFeedbackSlide,
  quillsAIIsUniqueSlide,
  ourAimSocialStudiesSlide,
  <FinalSlide getPremiumCredit={getPremiumCredit} key="final-slide" />,
]

export default socialStudiesSlides
